import { BOOKING_PORTAL_REFERRER_SOURCE } from "../constants";
import { PATH_HOME } from "../constants/urlPaths";

export const BUSINESS_PORTAL_PATH = "/business/";

// The external admin portal and the in-SPA BusinessApp use different route
// names for the same page (e.g. admin `/users` is `user-management` in the
// BusinessApp tab config). Map the known differences so internal links resolve
// instead of falling through to the default tab.
const ADMIN_TO_BUSINESS_ROUTE: Record<string, string> = {
  "/users": "user-management",
  "/business-profile": "loyalty-programs",
  "/out-of-policy-bookings": "out-of-policy",
};

/**
 * Returns the in-SPA `/business/` destination for an admin link, translating
 * known route-name differences and preserving the referrer source and any
 * extra query params.
 */
export const getBusinessUrl = (
  path: string | undefined,
  extraQuery?: string
): string => {
  const suffix = path
    ? ADMIN_TO_BUSINESS_ROUTE[path] ?? path.replace(/^\//, "")
    : "";
  const query = [BOOKING_PORTAL_REFERRER_SOURCE, extraQuery]
    .filter(Boolean)
    .join("&");

  return `${BUSINESS_PORTAL_PATH}${suffix}?${query}`;
};

const KNOWN_ADMIN_PATHS: Record<string, string> = {
  "": "",
  "/reports": "reports",
  "/travel-policies": "travel-policies",
  ...ADMIN_TO_BUSINESS_ROUTE,
};

// The admin trips page was the business-wide view, i.e. the trips page's
// "All trips" scope tab.
const ALL_TRIPS = {
  pathname: `${PATH_HOME}trips/`,
  query: { scope: "allTrips" },
};

const ADMIN_TO_PORTAL_PATH: Record<
  string,
  { pathname: string; query?: Record<string, string> }
> = {
  "/trips": ALL_TRIPS,
  "/all-trips": ALL_TRIPS,
  "/onboarding": { pathname: PATH_HOME },
};

const resolveAdminPath = (adminPath: string) => {
  const portalPath = ADMIN_TO_PORTAL_PATH[adminPath];
  if (portalPath != null) return portalPath;

  const tab = KNOWN_ADMIN_PATHS[adminPath] ?? "";
  return { pathname: `${BUSINESS_PORTAL_PATH}${tab}${tab ? "/" : ""}` };
};

/**
 * Maps a legacy `/admin...` URL (e.g. from a bookmark) to where that page lives
 * now in the portal, keeping the original query string. Unknown admin paths
 * fall back to the business landing page.
 */
export const getRedirectPathForAdminPath = (
  pathname: string,
  search = ""
): string => {
  const adminPath = pathname.replace(/^\/admin/, "").replace(/\/$/, "");
  const { pathname: target, query = {} } = resolveAdminPath(adminPath);

  const params = new URLSearchParams(search);
  for (const [key, value] of Object.entries(query)) params.set(key, value);
  const queryString = params.toString();

  return queryString ? `${target}?${queryString}` : target;
};
